import logo from "../../assets/images/logo-hacom-2024.png";
import logoDVT from "../../assets/images/logoDVT.svg";

/**
 * Phiếu xuất trả khách của Dịch vụ sửa chữa.
 *
 * Tách riêng khỏi `Warranty/ReceiveWarranty/PrinterReturnCustomers` vì nghiệp vụ khác:
 * phiếu này gom nhiều dòng bảng kê **cùng một mã phiếu dịch vụ** và có thể thuộc **nhiều
 * phiếu xuất trả** khác nhau. Đọc thẳng dữ liệu của API
 * `DefectiveItemsList/print-return-customer-service`.
 */
const PrinterServiceReturnCustomers = ({
  innerRef,
  dataPrint,
}: {
  innerRef: any;
  dataPrint: any;
}) => {
  const items: any[] = dataPrint?.items ?? [];
  // Ghi chú của từng phiếu xuất trả, mỗi phiếu một dòng
  const outwardNotes: any[] = dataPrint?.outwardNotes ?? [];

  const pad = (n: number) => String(n).padStart(2, "0");
  const today = new Date();

  const formatDate = (value: any) => {
    if (!value) return "";
    const d = new Date(value);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  };

  const formatDateTime = (value: any) => {
    if (!value) return "";
    const d = new Date(value);
    return `${pad(d.getHours())}:${pad(d.getMinutes())} ${formatDate(d)}`;
  };

  const userLogin = JSON.parse(
    localStorage.getItem("userLoginHeader") || "{}",
  );

  // Pháp nhân quyết định logo và thông tin liên hệ trên đầu phiếu
  const isDvt = dataPrint?.companyCode === "DVT";

  const cell: React.CSSProperties = {
    border: "1px solid #000",
    padding: "4px 6px",
    fontSize: 11,
    textAlign: "center",
    verticalAlign: "middle",
  };
  const head: React.CSSProperties = { ...cell, fontWeight: 700 };
  const label: React.CSSProperties = { fontWeight: 700 };

  return (
    <div
      ref={innerRef}
      style={{
        padding: 24,
        color: "#000",
        fontFamily: "'Times New Roman', serif",
        fontSize: 12,
      }}
    >
      {/* ===== Đầu phiếu ===== */}
      {/* Logo bên trái, khối thông tin công ty đẩy sát lề phải như mẫu giấy */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 24,
        }}
      >
        <img
          src={isDvt ? logoDVT : logo}
          alt={isDvt ? "logoDVT" : "logo Hacom"}
          width={isDvt ? 225 : 150}
          height={isDvt ? 50 : "auto"}
          style={{ objectFit: "contain" }}
        />
        <div style={{ lineHeight: 1.6 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>
            {isDvt
              ? "CÔNG TY CỔ PHẦN HACOM DỊCH VỤ TỐT"
              : "CÔNG TY CỔ PHẦN ĐẦU TƯ CÔNG NGHỆ HACOM"}
          </h3>
          <p style={{ margin: 0 }}>
            <span style={label}>Chi nhánh: </span>
            {dataPrint?.branchName}
          </p>
          <p style={{ margin: 0 }}>
            <span style={label}>Địa chỉ: </span>
            {dataPrint?.branchAddress}
          </p>
          {/* Liên hệ lấy theo danh mục chi nhánh TBL_DM_MASTER_ORG:
              PHONE → Tel, ATTRIBUTE1 → Máy lẻ, EMAIL → Email */}
          <p style={{ margin: 0 }}>
            <span style={label}>Tel: </span>
            {dataPrint?.branchPhone}
            <span style={{ marginLeft: 24 }}>
              <span style={label}>Máy lẻ: </span>
              {dataPrint?.branchExtension}
            </span>
          </p>
          <p style={{ margin: 0 }}>
            <span style={label}>Email: </span>
            {dataPrint?.branchEmail}
          </p>
        </div>
      </div>

      <hr
        style={{
          margin: "10px 0",
          border: 0,
          borderTop: `2px solid ${isDvt ? "#e03131" : "#000"}`,
        }}
      />

      {/* ===== Tiêu đề ===== */}
      <div style={{ textAlign: "center" }}>
        <h2 style={{ margin: 0, fontSize: 17 }}>PHIẾU XUẤT TRẢ SẢN PHẨM</h2>
        <p style={{ margin: "4px 0 0", fontStyle: "italic" }}>
          Ngày {pad(today.getDate())} tháng {pad(today.getMonth() + 1)} năm{" "}
          {today.getFullYear()}
        </p>
        <p style={{ margin: 0 }}>Mã: {dataPrint?.serviceTicketCode}</p>
      </div>

      {/* ===== Thông tin khách hàng ===== */}
      <div style={{ marginTop: 12, lineHeight: 1.9 }}>
        <p style={{ margin: 0 }}>
          <span style={label}>Khách hàng: </span>
          {dataPrint?.customerName}
        </p>
        <p style={{ margin: 0 }}>
          <span style={label}>Địa chỉ: </span>
          {dataPrint?.customerAddress}
        </p>
        <p style={{ margin: 0 }}>
          <span style={label}>Nhân viên xuất trả: </span>
          {dataPrint?.outwardEmployee}
          <span style={{ marginLeft: 32 }}>
            <span style={label}>Ngày xuất trả: </span>
            {formatDateTime(dataPrint?.outwardDate)}
          </span>
        </p>
        <p style={{ margin: 0 }}>
          <span style={label}>Người nhận: </span>
          {dataPrint?.customerName}
          <span style={{ marginLeft: 32 }}>
            <span style={label}>Số điện thoại: </span>
            {dataPrint?.customerPhone}
          </span>
        </p>
        <div style={{ display: "flex" }}>
          <span style={{ ...label, whiteSpace: "nowrap" }}>Diễn giải:</span>
          <div style={{ marginLeft: 4 }}>
            {outwardNotes.map((noteItem: any, index: number) => (
              <p key={noteItem?.outwardCode ?? index} style={{ margin: 0 }}>
                {/* Chỉ một phiếu có ghi chú thì không cần nêu mã phiếu */}
                {outwardNotes.length > 1
                  ? `${noteItem?.outwardCode}: ${noteItem?.note}`
                  : noteItem?.note}
              </p>
            ))}
          </div>
        </div>
      </div>

      <p style={{ fontStyle: "italic", fontWeight: 700, marginTop: 12 }}>
        Dưới đây là bảng kê chi tiết sản phẩm trả cho quý khách hàng:
      </p>

      {/* ===== Bảng sản phẩm ===== */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={head} rowSpan={2}>
              STT
            </th>
            <th style={head} colSpan={3}>
              Sản phẩm nhận
            </th>
            <th style={head} rowSpan={2}>
              Ngày mua
            </th>
            <th style={head} rowSpan={2}>
              Mã phiếu nhập
            </th>
            <th style={head} colSpan={3}>
              Sản phẩm trả
            </th>
            <th style={head} rowSpan={2}>
              Loại dịch vụ
            </th>
            <th style={head} rowSpan={2}>
              Ghi chú
            </th>
          </tr>
          <tr>
            <th style={head}>Tên sản phẩm</th>
            <th style={head}>Serial</th>
            <th style={head}>ĐVT</th>
            <th style={head}>Tên sản phẩm</th>
            <th style={head}>Serial</th>
            <th style={head}>ĐVT</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item: any, index: number) => (
            <tr key={item?.defectiveId ?? index}>
              <td style={cell}>{index + 1}</td>
              <td style={cell}>{item?.itemName}</td>
              <td style={cell}>{item?.serial}</td>
              <td style={cell}>{item?.uomName}</td>
              <td style={cell}>{formatDate(item?.buyingDate)}</td>
              <td style={cell}>{item?.inwardCode}</td>
              <td style={cell}>{item?.returnItemName}</td>
              <td style={cell}>{item?.returnSerial}</td>
              <td style={cell}>{item?.uomName}</td>
              <td style={cell}>{item?.serviceType}</td>
              <td style={cell}>{item?.lineNote}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...cell, fontWeight: 700 }}>Tổng cộng</td>
            <td style={{ ...cell, fontWeight: 700 }} colSpan={3}>
              {items.length}
            </td>
            <td style={cell}></td>
            <td style={cell}></td>
            <td style={{ ...cell, fontWeight: 700 }} colSpan={3}>
              {items.length}
            </td>
            <td style={cell}></td>
            <td style={cell}></td>
          </tr>
        </tbody>
      </table>

      <p style={{ fontSize: 11, marginTop: 12 }}>
        <span style={label}>Lưu ý: </span>
        Quý khách kiểm tra kỹ thông số, cấu hình, tình trạng, số lượng hàng hóa
        đầy đủ khi nhận lại hàng (linh kiện, phụ kiện đi kèm, đĩa cài, sách, vỏ
        hộp,... nếu có).
      </p>

      {/* ===== Chữ ký ===== */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          marginTop: 28,
          textAlign: "center",
        }}
      >
        <div>
          <p style={{ ...label, margin: 0 }}>Người lập phiếu</p>
          <p style={{ fontStyle: "italic", margin: 0 }}>
            (Ký &amp; ghi rõ họ tên)
          </p>
        </div>
        <div>
          <p style={{ ...label, margin: 0 }}>Người nhận hàng</p>
          <p style={{ fontStyle: "italic", margin: 0 }}>
            (Ký &amp; ghi rõ họ tên)
          </p>
        </div>
      </div>

      {/* ===== Chân phiếu ===== */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 20,
          marginTop: 120,
          fontSize: 11,
        }}
      >
        <p style={{ margin: 0 }}>
          Nhân viên in phiếu: {userLogin?.code} - {userLogin?.fullname}
        </p>
        <p style={{ margin: 0 }}>Ngày in phiếu: {formatDateTime(today)}</p>
      </div>
    </div>
  );
};

export default PrinterServiceReturnCustomers;
